import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { VehicleCard } from "../components/VehicleCard";
import { ArrowRight, ShieldCheck, Clock, MessageCircle } from "lucide-react";
import { ContactWidget } from "../components/ContactWidget";
import { CookieBanner } from "../components/CookieBanner";
import { Link } from "react-router-dom";
import { PHONE_NUMBERS, WHATSAPP_NUMBERS, CONTACT_EMAIL } from "../lib/api";
import { track } from "../lib/track";

const SORTS = {
  price_desc: { label: "Prezzo decrescente", fn: (a, b) => b.price - a.price },
  price_asc: { label: "Prezzo crescente", fn: (a, b) => a.price - b.price },
  newest: { label: "Più recenti", fn: (a, b) => (b.year - a.year) || (b.created_at || "").localeCompare(a.created_at || "") },
  km_asc: { label: "Chilometraggio crescente", fn: (a, b) => a.km - b.km },
};

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("price_desc");
  const sorted = [...vehicles].sort(SORTS[sort].fn);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Header transparent contact={false} />

      {/* Floating WhatsApp contact */}
      <ContactWidget message="Ciao Fantini Next Mobility! Vorrei informazioni sulle vetture disponibili." />

      {/* Hero */}
      <section className="relative min-h-screen flex items-end overflow-hidden bg-[#f3f1ea]">
        <img
          src="/hero-car.jpg"
          alt="Fantini Next Mobility — set fotografico"
          className="absolute inset-x-0 top-0 h-[54vh] w-full object-cover object-[62%_60%] md:inset-0 md:h-full md:object-center"
        />
        <div className="absolute inset-x-0 top-[34vh] h-[22vh] md:hidden" style={{ background: "linear-gradient(to bottom, rgba(243,241,234,0) 0%, rgba(243,241,234,0.85) 55%, #f3f1ea 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pb-28 md:pb-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="text-xs font-medium uppercase tracking-[0.18em] text-forest">Fantini Next Mobility</span>
            </div>
            <h1 className="font-serif text-[2.35rem] sm:text-4xl md:text-5xl font-light text-ink leading-[1.08]">
              Partiamo da te.<br />Troviamo la tua auto.
            </h1>
            <p className="text-ink-soft text-base md:text-lg mt-5 md:mt-6 max-w-md font-light">
              Un percorso semplice e guidato per scegliere, configurare e bloccare la vettura giusta.
            </p>
            <a
              href="#catalogo"
              data-testid="hero-cta"
              className="inline-flex items-center gap-2 mt-8 px-7 py-4 rounded-full bg-ink text-white text-sm hover:opacity-90 transition-opacity"
            >
              Inizia il percorso <ArrowRight size={16} strokeWidth={1.5} className="text-[#68b39d]" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 grid sm:grid-cols-3 gap-6">
        {[
          { icon: Clock, t: "In pochi minuti", d: "Completa il percorso di acquisto dal tuo telefono, quando vuoi." },
          { icon: ShieldCheck, t: "Blocco sicuro", d: "Prenota la vettura con un deposito rimborsabile." },
          { icon: MessageCircle, t: "Consulente reale", d: "Un venditore Fantini ti ricontatta per finalizzare." },
        ].map((f, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="p-7 bg-white rounded-3xl shadow-ambient">
            <f.icon size={22} strokeWidth={1.5} className="text-sand" />
            <h3 className="font-serif text-xl mt-4">{f.t}</h3>
            <p className="text-sm text-ink-soft mt-1.5 leading-relaxed">{f.d}</p>
          </motion.div>
        ))}
      </section>

      {/* Catalog */}
      <section id="catalogo" className="max-w-6xl mx-auto px-5 md:px-8 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-sand mb-2">Il catalogo</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Vetture selezionate</h2>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <span className="text-ink-soft whitespace-nowrap">Ordina per</span>
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
              className="h-11 pl-4 pr-9 rounded-full border border-border bg-white outline-none focus:border-ink appearance-none bg-no-repeat bg-[right_0.9rem_center] bg-[length:14px]"
              style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%231C1C1A' stroke-width='1.5'><path d='m6 9 6 6 6-6'/></svg>\")" }}>
              {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select>
          </label>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-white rounded-3xl animate-pulse" />)}
          </div>
        ) : (
          <div data-testid="vehicle-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
          </div>
        )}
      </section>

      <footer className="bg-ink text-white/60 py-14">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <div className="font-serif text-2xl text-white tracking-wider">FANTINI NEXT MOBILITY</div>
            <p className="text-sm mt-2 max-w-sm">Un'esperienza di acquisto digitale nuova nel settore automobilistico.</p>
          </div>
          <div className="text-sm space-y-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#68b39d] mb-2">I nostri contatti</div>
            {PHONE_NUMBERS.map((p) => (
              <a key={p.tel} href={`tel:${p.tel}`} onClick={() => track("click_phone", { label: p.sub })} data-testid={`footer-phone-${p.tel}`} className="block hover:text-white transition-colors">Tel. {p.label} <span className="text-white/40">· {p.sub}</span></a>
            ))}
            {WHATSAPP_NUMBERS.map((w) => (
              <a key={w.number} href={`tel:+${w.number}`} onClick={() => track("click_phone", { label: "cellulare" })} data-testid={`footer-cell-${w.number}`} className="block hover:text-white transition-colors">Cell. {w.label}</a>
            ))}
            <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => track("click_email")} data-testid="footer-email" className="block hover:text-white transition-colors pt-1">{CONTACT_EMAIL}</a>
          </div>
          <div className="text-sm space-y-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#68b39d] mb-2">Informazioni legali</div>
            <Link to="/privacy-policy" data-testid="footer-privacy" className="block hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" data-testid="footer-cookie" className="block hover:text-white transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </footer>
      <CookieBanner />
    </div>
  );
}
